import { api } from './api';
import { ApiError } from './api';
import {
  isPermanentWriteRejection,
  isStaleBaseError,
  saveDeliverables,
} from './deliverablesService';
import {
  flushAndRewriteRichHtmlRecord,
  stripPendingRefsInRecord,
} from './flushOfflineInlineImageQueue';
import {
  isLocationTagNameConflictError,
  mirrorLocationTagsToOfflineCache,
} from './locationTagOfflineSync';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { clearCachedUnseenChanges } from './requestReadMarker';
import { RequestsService } from './requestsService';
import { userProfileService } from './userProfileService';
import { CoolingSystemConfiguration } from '../types/coolingSystem';
import { LargeUpsConfiguration } from '../types/largeUps';
import { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import { PowerDistributionConfiguration } from '../types/powerDistribution';
import { RackConfiguration } from '../types/rack';
import { SmallUpsConfiguration } from '../types/smallUps';
import { normalizeSyncedRequestPayload } from '../utils/normalizeSyncedRequestPayload';
import { prepareTextAreaFieldsForTransport } from '../utils/richContent/sanitizeTextArea';
import { saveTeamMembersFromForm } from '../utils/teamMemberUtils';

import type { LocationTagsResponse } from '../types/locationTag';

export interface SyncResult {
  success: boolean;
  syncedCount: number;
  failedCount: number;
  errors: string[];
  teamMemberSyncFailures?: Array<{
    requestId: string;
    error: string;
  }>;
  /**
   * Deliverable lists that failed to replay.
   *
   * **No longer the "Option A" contract** `teamMemberSyncFailures` uses. That contract —
   * count the request as synced, record the failure here, drain the entry anyway — meant a
   * rejected list was on neither the server nor the device, and this array had no reader, so
   * the loss was total and silent. A deliverables failure now marks the queue entry `failed`
   * and keeps the list, counts toward `failedCount`, and surfaces through the existing sync
   * indicator. This array stays as the detail behind that count.
   * (MR !489 review, finding 01 / the silent-replay-failure cluster.)
   */
  deliverableSyncFailures?: Array<{
    requestId: string;
    error: string;
  }>;
  profileSynced?: boolean;
  rackOperationsSynced?: number;
  rackOperationsFailed?: number;
  smallUpsOperationsSynced?: number;
  smallUpsOperationsFailed?: number;
  coolingSystemOperationsSynced?: number;
  coolingSystemOperationsFailed?: number;
  liquidCoolingCduOperationsSynced?: number;
  liquidCoolingCduOperationsFailed?: number;
  largeUpsOperationsSynced?: number;
  largeUpsOperationsFailed?: number;
  powerDistributionOperationsSynced?: number;
  powerDistributionOperationsFailed?: number;
  locationTagOperationsSynced?: number;
  locationTagOperationsFailed?: number;
  /** Request details whose temp location-tag assignments were remapped. */
  locationTagRequestIdsSynced?: string[];
  /** Tag names that failed to sync due to server-side uniqueness conflicts (#999). */
  locationTagNameConflicts?: string[];
  /**
   * Offline-create requests that were assigned a real server id on sync.
   * Lets the UI redirect away from the now-dead `o-*` request URL.
   */
  requestIdMappings?: Array<{ offlineId: string; serverId: string }>;
  /** Read markers (#1575) the server accepted on replay. */
  readOperationsSynced?: number;
  /**
   * Read markers kept for the next pass (offline mid-drain, or a 5xx). Counted
   * separately from `droppedCount`-style permanent refusals below because they
   * are not failures of anything the user did — the marker is still queued.
   */
  readOperationsFailed?: number;
  /**
   * Read markers the server refused permanently (a 4xx, `INVALID_READ_AT`
   * included) and that were therefore removed from the queue rather than retried.
   */
  readOperationsDropped?: number;
}

/**
 * Service for syncing offline data when connection is restored
 * Handles uploading queued requests and updating cache
 */
class SyncService {
  private isSyncing = false;
  private syncListeners: ((result: SyncResult) => void)[] = [];
  private syncTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly SYNC_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes max sync time

  /**
   * Manual sync trigger - sync all pending offline requests
   */
  syncOfflineRequests = async (): Promise<SyncResult> => {
    if (this.isSyncing) {
      console.log('Sync already in progress');
      return {
        success: false,
        syncedCount: 0,
        failedCount: 0,
        errors: ['Sync already in progress'],
      };
    }

    if (!networkService.isConnected()) {
      console.log('Cannot sync - no network connection');
      return {
        success: false,
        syncedCount: 0,
        failedCount: 0,
        errors: ['No network connection'],
      };
    }

    this.isSyncing = true;

    // Set a timeout to auto-reset if sync gets stuck
    this.syncTimeoutId = setTimeout(() => {
      console.error(
        '⚠️ SyncService: Sync timeout - forcing reset after',
        this.SYNC_TIMEOUT_MS / 1000,
        'seconds',
      );
      this.isSyncing = false;
      this.syncTimeoutId = null;
    }, this.SYNC_TIMEOUT_MS);

    const result: SyncResult = {
      success: true,
      syncedCount: 0,
      failedCount: 0,
      errors: [],
      teamMemberSyncFailures: [],
      deliverableSyncFailures: [],
      requestIdMappings: [],
    };

    try {
      console.log('Starting offline sync...');

      // Get pending offline requests (new creates)
      const pendingRequests = await offlineCache.getPendingOfflineRequests();

      if (pendingRequests.length === 0) {
        console.log('No offline create requests to sync');
      } else {
        console.log(
          `Syncing ${pendingRequests.length} offline create requests...`,
        );

        // Sync each request
        for (const offlineRequest of pendingRequests) {
          try {
            // Update status to syncing
            await offlineCache.updateOfflineRequestStatus(
              offlineRequest.id,
              'syncing',
            );

            // Attempt to create the request on the server
            const normalizedPayload = normalizeSyncedRequestPayload(
              offlineRequest.data as Record<string, unknown>,
            );
            const createPayload = prepareTextAreaFieldsForTransport(
              stripPendingRefsInRecord(normalizedPayload),
            );
            const createdRequest = await RequestsService.createRequest(
              createPayload,
            );

            const resolvedPayload = prepareTextAreaFieldsForTransport(
              await flushAndRewriteRichHtmlRecord(
                normalizedPayload,
                Number(createdRequest.id),
              ),
            );
            if (
              JSON.stringify(resolvedPayload) !== JSON.stringify(createPayload)
            ) {
              await RequestsService.updateRequest(
                String(createdRequest.id),
                resolvedPayload,
              );
            }

            console.log(
              `Successfully synced offline request: ${offlineRequest.id} -> ${createdRequest.id}`,
            );

            // Record the offline -> server id remap so the UI can redirect away
            // from the dead `o-*` request URL the user may be sitting on.
            result.requestIdMappings?.push({
              offlineId: offlineRequest.id,
              serverId: String(createdRequest.id),
            });

            // CRITICAL: Immediately update cache with the synced request to prevent it from disappearing
            // This ensures the UI shows the request continuously (offline ID -> real ID transition)
            const { requests: cachedRequests } =
              await offlineCache.getCachedRequests();
            // Remove the offline version if it exists in cache, then add the synced version at the top
            const filteredCache = cachedRequests.filter(
              req => req.id !== offlineRequest.id,
            );
            const updatedCache = [createdRequest, ...filteredCache];
            await offlineCache.cacheRequests(updatedCache);
            console.log(
              `✅ Added synced request ${createdRequest.id} to cache immediately and removed offline placeholder`,
            );

            // Option A: Try to sync team members, but don't fail the entire request if team members fail
            if (
              offlineRequest.teamMembers &&
              (offlineRequest.teamMembers.vertivSponsors?.length ||
                offlineRequest.teamMembers.vertivContributors?.length ||
                offlineRequest.teamMembers.partnerContributors?.length)
            ) {
              try {
                console.log(
                  `Syncing team members for request ${createdRequest.id}:`,
                  offlineRequest.teamMembers,
                );
                await saveTeamMembersFromForm(
                  createdRequest.id,
                  offlineRequest.teamMembers,
                );
                console.log(
                  `Successfully synced team members for request ${createdRequest.id}`,
                );
              } catch (teamError) {
                // Team member sync failed, but request succeeded
                const teamErrorMessage =
                  teamError instanceof Error
                    ? teamError.message
                    : 'Unknown error';
                console.error(
                  `Failed to sync team members for request ${createdRequest.id}:`,
                  teamError,
                );

                result.teamMemberSyncFailures?.push({
                  requestId: createdRequest.id,
                  error: teamErrorMessage,
                });

                // Log warning but don't fail the entire sync
                console.warn(
                  `Request ${offlineRequest.id} synced successfully but team members failed. User can add team members manually.`,
                );
              }
            }

            // Option A again, for deliverables. `!== undefined` and not `.length`: an
            // empty list is a real value the user chose and still has to be replayed.
            if (offlineRequest.deliverables !== undefined) {
              try {
                // An honest empty base, not a placeholder: this request was created seconds
                // ago on this very code path, so it holds no deliverables and every row in
                // the list is an addition. Nothing here can be a deletion, so an empty proof
                // costs nothing. (MR !489 review, batch 1b.)
                await saveDeliverables(
                  createdRequest.id,
                  [],
                  offlineRequest.deliverables,
                );
                // The list now belongs to the real request, so move it onto that cache
                // entry. Skipping this leaves the freshly cached server request with no
                // deliverables key, and the next offline edit would build on an empty base.
                await offlineCache.updateCachedRequestDeliverables(
                  createdRequest.id,
                  offlineRequest.deliverables,
                );
                console.log(
                  `Successfully synced deliverables for request ${createdRequest.id}`,
                );
              } catch (deliverableError) {
                const deliverableErrorMessage =
                  deliverableError instanceof Error
                    ? deliverableError.message
                    : 'Unknown error';
                console.error(
                  `Failed to sync deliverables for request ${createdRequest.id}:`,
                  deliverableError,
                );
                result.deliverableSyncFailures?.push({
                  requestId: createdRequest.id,
                  error: deliverableErrorMessage,
                });

                // Re-queue against the REAL id rather than retaining the create entry.
                // The request itself was created successfully, so keeping the create entry
                // would make the next sync create a second one. Queueing an update means the
                // next sync retries only the part that failed.
                //
                // Without this the list was lost outright: the create entry is drained a few
                // statements below, and the cache was deliberately not updated on this
                // branch. (MR !489 review, finding 01.)
                const requeued = await offlineCache.queueDeliverables(
                  createdRequest.id,
                  // Still an honest empty base. The write is transactional, so a failure
                  // means nothing landed and the new request still holds no deliverables.
                  [],
                  offlineRequest.deliverables,
                );
                result.errors.push(
                  requeued
                    ? `Deliverables for request ${createdRequest.id} were not saved and have been queued for retry.`
                    : `Deliverables for request ${createdRequest.id} were not saved and could not be queued.`,
                );
              }
            }

            // CRITICAL: Remap rack queue entries from old offline ID to real server ID
            // This ensures any queued rack operations for this request use the real ID
            const remappedRackOps = await offlineCache.remapRackQueueRequestIds(
              offlineRequest.id,
              createdRequest.id,
            );
            if (remappedRackOps > 0) {
              console.log(
                `✅ Remapped ${remappedRackOps} rack queue entries from ${offlineRequest.id} to ${createdRequest.id}`,
              );
            }

            // CRITICAL: Remap small UPS queue entries from offline ID to server ID (#996)
            const remappedSmallUpsOps =
              await offlineCache.remapSmallUpsQueueRequestIds(
                offlineRequest.id,
                createdRequest.id,
              );
            if (remappedSmallUpsOps > 0) {
              console.log(
                `✅ Remapped ${remappedSmallUpsOps} small UPS queue entries from ${offlineRequest.id} to ${createdRequest.id}`,
              );
            }

            // CRITICAL: Remap cooling system queue entries from offline ID to server ID (#996 Phase 2)
            const remappedCoolingSystemOps =
              await offlineCache.remapCoolingSystemQueueRequestIds(
                offlineRequest.id,
                createdRequest.id,
              );
            if (remappedCoolingSystemOps > 0) {
              console.log(
                `✅ Remapped ${remappedCoolingSystemOps} cooling system queue entries from ${offlineRequest.id} to ${createdRequest.id}`,
              );
            }

            // CRITICAL: Remap liquid cooling CDU queue entries from offline ID to server ID (#996 Phase 3)
            const remappedLiquidCoolingCduOps =
              await offlineCache.remapLiquidCoolingCduQueueRequestIds(
                offlineRequest.id,
                createdRequest.id,
              );
            if (remappedLiquidCoolingCduOps > 0) {
              console.log(
                `✅ Remapped ${remappedLiquidCoolingCduOps} liquid cooling CDU queue entries from ${offlineRequest.id} to ${createdRequest.id}`,
              );
            }

            // CRITICAL: Remap large UPS queue entries from offline ID to server ID (#996 Phase 4)
            const remappedLargeUpsOps =
              await offlineCache.remapLargeUpsQueueRequestIds(
                offlineRequest.id,
                createdRequest.id,
              );
            if (remappedLargeUpsOps > 0) {
              console.log(
                `✅ Remapped ${remappedLargeUpsOps} large UPS queue entries from ${offlineRequest.id} to ${createdRequest.id}`,
              );
            }

            // CRITICAL: Remap power distribution queue entries from offline ID to server ID (#996)
            const remappedPowerDistributionOps =
              await offlineCache.remapPowerDistributionQueueRequestIds(
                offlineRequest.id,
                createdRequest.id,
              );
            if (remappedPowerDistributionOps > 0) {
              console.log(
                `✅ Remapped ${remappedPowerDistributionOps} power distribution queue entries from ${offlineRequest.id} to ${createdRequest.id}`,
              );
            }

            const remappedLocationTagOps =
              await offlineCache.remapLocationTagQueueRequestIds(
                offlineRequest.id,
                createdRequest.id,
              );
            if (remappedLocationTagOps > 0) {
              console.log(
                `✅ Remapped ${remappedLocationTagOps} location tag queue entries from ${offlineRequest.id} to ${createdRequest.id}`,
              );
            }

            // Success - remove from offline queue AFTER cache is updated (even if team members failed)
            // This prevents race condition where request disappears before cache is updated
            await offlineCache.removeFromOfflineQueue(offlineRequest.id);
            result.syncedCount++;

            // Verify the synced request is in cache before continuing
            const verifyCache = await offlineCache.getCachedRequestById(
              createdRequest.id,
            );
            if (verifyCache) {
              console.log(
                `✅ VERIFIED: Request ${createdRequest.id} is in cache after sync`,
              );
            } else {
              console.error(
                `⚠️ WARNING: Request ${createdRequest.id} NOT in cache after sync - re-adding`,
              );
              const { requests: currentCache } =
                await offlineCache.getCachedRequests();
              await offlineCache.cacheRequests([
                createdRequest,
                ...currentCache,
              ]);
            }
          } catch (error) {
            // Failure - mark as failed
            await offlineCache.updateOfflineRequestStatus(
              offlineRequest.id,
              'failed',
            );
            result.failedCount++;

            const errorMessage =
              error instanceof Error ? error.message : 'Unknown error';
            result.errors.push(
              `Failed to sync ${offlineRequest.id}: ${errorMessage}`,
            );

            console.error(
              `Failed to sync offline request ${offlineRequest.id}:`,
              error,
            );
          }
        }
      }

      // Sync pending updates.
      //
      // `failed` entries are skipped, and the filter is load-bearing rather than tidiness.
      // `markQueuedDeliverablesFailed` is the only writer of that status, so the only entries
      // it excludes are deliverables the server has already rejected. Without it a permanent
      // rejection — a stale base, which cannot succeed however many times it is sent — was
      // re-sent on every sync forever, and each attempt re-reported the same failure to the
      // user. The status was previously read only on the client's own read path, never here,
      // which is why the retry loop survived. (MR !489 review, batch 1b.)
      const pendingUpdates = (
        await offlineCache.getOfflineUpdateQueue()
      ).filter(update => update.status !== 'failed');

      if (pendingUpdates.length > 0) {
        console.log(`Syncing ${pendingUpdates.length} offline updates...`);

        for (const offlineUpdate of pendingUpdates) {
          try {
            // Attempt to update the request on the server
            const normalizedPayload = normalizeSyncedRequestPayload(
              offlineUpdate.data as Record<string, unknown>,
            );
            const flushedPayload = await flushAndRewriteRichHtmlRecord(
              normalizedPayload,
              Number.parseInt(offlineUpdate.requestId, 10),
            );
            const updatePayload =
              prepareTextAreaFieldsForTransport(flushedPayload);
            await RequestsService.updateRequest(
              offlineUpdate.requestId,
              updatePayload,
            );

            console.log(
              `Successfully synced offline update for request: ${offlineUpdate.requestId}`,
            );

            // Option A: Try to sync team member updates if present
            if (
              offlineUpdate.teamMembers &&
              (offlineUpdate.teamMembers.vertivSponsors !== undefined ||
                offlineUpdate.teamMembers.vertivContributors !== undefined ||
                offlineUpdate.teamMembers.partnerContributors !== undefined)
            ) {
              try {
                console.log(
                  `Syncing team member updates for request ${offlineUpdate.requestId}:`,
                  offlineUpdate.teamMembers,
                );
                await saveTeamMembersFromForm(
                  offlineUpdate.requestId,
                  offlineUpdate.teamMembers,
                );
                console.log(
                  `Successfully synced team member updates for request ${offlineUpdate.requestId}`,
                );
              } catch (teamError) {
                // Team member sync failed, but request update succeeded
                const teamErrorMessage =
                  teamError instanceof Error
                    ? teamError.message
                    : 'Unknown error';
                console.error(
                  `Failed to sync team member updates for request ${offlineUpdate.requestId}:`,
                  teamError,
                );

                result.teamMemberSyncFailures?.push({
                  requestId: offlineUpdate.requestId,
                  error: teamErrorMessage,
                });

                console.warn(
                  `Request ${offlineUpdate.requestId} update synced successfully but team members failed.`,
                );
              }
            }

            // Option A again, for deliverables. One batch PUT of the whole intended list;
            // the server diffs it. `!== undefined` so a clear-to-empty is replayed too.
            let deliverablesRejected = false;
            let deliverablesStale = false;
            let deliverablesPermanent = false;
            if (offlineUpdate.deliverables !== undefined) {
              try {
                // The base stored when the edit was made, which is the only thing that can
                // authorise the removals in it. `[]` used to be sent here and was simply
                // untrue; now that the server checks, sending it would refuse every
                // legitimate offline removal. An entry queued before the field existed has
                // none, and `[]` is then the honest answer: it proves nothing, so the adds
                // and comment edits replay and the removals do not.
                await saveDeliverables(
                  offlineUpdate.requestId,
                  offlineUpdate.deliverablesBase ?? [],
                  offlineUpdate.deliverables,
                );
                // Keep the cached base in step with what the server now holds — the queue
                // entry that was carrying this list is about to be drained.
                await offlineCache.updateCachedRequestDeliverables(
                  offlineUpdate.requestId,
                  offlineUpdate.deliverables,
                );
                console.log(
                  `Successfully synced deliverables for request ${offlineUpdate.requestId}`,
                );
              } catch (deliverableError) {
                const deliverableErrorMessage =
                  deliverableError instanceof Error
                    ? deliverableError.message
                    : 'Unknown error';
                console.error(
                  `Failed to sync deliverables for request ${offlineUpdate.requestId}:`,
                  deliverableError,
                );
                result.deliverableSyncFailures?.push({
                  requestId: offlineUpdate.requestId,
                  error: deliverableErrorMessage,
                });
                // The list is on neither the server nor anywhere else once this entry is
                // drained below, and the cache was deliberately not updated in this branch.
                // Keep it so a retry is possible and the loss is not silent.
                deliverablesRejected = true;
                // A stale base and a dropped connection need opposite remedies, so they must
                // not be collapsed into one message: retrying a stale base can never work,
                // and telling the user to reload over a flaky network would be wrong.
                // (MR !489 review, AC1.8.3.)
                deliverablesStale = isStaleBaseError(deliverableError);
                // Whether to retry is a wider question than whether to reload. Every 4xx is
                // final — an unknown type, a request deleted while offline, a refused base —
                // so re-sending it just reproduces the same error on every sync. Only a 5xx
                // or a transport failure is worth another attempt.
                deliverablesPermanent =
                  isPermanentWriteRejection(deliverableError);
                result.errors.push(
                  deliverablesStale
                    ? `Deliverables for request ${offlineUpdate.requestId} were not saved: this request's deliverables changed while you were offline. Reload the request to see the current list, then make your change again.`
                    : `Deliverables for request ${offlineUpdate.requestId} were not saved: ${deliverableErrorMessage}`,
                );
              }
            }

            if (deliverablesRejected) {
              // Retain the deliverables, discard the parts that did land so a retry does not
              // write them twice. Counts as a failure: reporting a clean sync here is what
              // made this loss invisible. (MR !489 review.) Any 4xx is marked permanently
              // failed; a 5xx or a transport failure stays pending for the next sync.
              await offlineCache.markQueuedDeliverablesFailed(
                offlineUpdate.requestId,
                deliverablesPermanent,
              );
              result.failedCount++;
            } else {
              // Success - remove from offline update queue (even if team members failed)
              await offlineCache.removeFromOfflineUpdateQueue(
                offlineUpdate.requestId,
              );
              result.syncedCount++;
            }
          } catch (error) {
            result.failedCount++;

            const errorMessage =
              error instanceof Error ? error.message : 'Unknown error';
            result.errors.push(
              `Failed to sync update for ${offlineUpdate.requestId}: ${errorMessage}`,
            );

            console.error(
              `Failed to sync offline update for request ${offlineUpdate.requestId}:`,
              error,
            );
          }
        }
      }

      // Sync pending profile update
      const pendingProfileUpdate = await offlineCache.getPendingProfileUpdate();
      if (pendingProfileUpdate) {
        try {
          const updatedProfile = await userProfileService.updateProfile(
            pendingProfileUpdate.data,
          );
          await offlineCache.cacheUserProfile(updatedProfile);
          await offlineCache.removePendingProfileUpdate();
          result.syncedCount++;
          result.profileSynced = true;
        } catch (error) {
          result.failedCount++;
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          result.errors.push(`Failed to sync profile update: ${errorMessage}`);
          console.error('Failed to sync offline profile update:', error);
        }
      }

      // Sync offline location tag operations (#999) — before unit queues so temp
      // tag IDs exist on the server (and are remapped locally) before assignments sync.
      const locationTagSyncResult =
        await this.syncOfflineLocationTagOperations();
      result.locationTagOperationsSynced = locationTagSyncResult.synced;
      result.locationTagOperationsFailed = locationTagSyncResult.failed;
      result.locationTagRequestIdsSynced = locationTagSyncResult.requestIds;
      result.locationTagNameConflicts = locationTagSyncResult.nameConflicts;
      if (locationTagSyncResult.errors.length > 0) {
        result.errors.push(...locationTagSyncResult.errors);
      }
      result.syncedCount += locationTagSyncResult.synced;
      result.failedCount += locationTagSyncResult.failed;

      // Sync offline rack operations (#995)
      const rackSyncResult = await this.syncOfflineRackOperations();
      result.rackOperationsSynced = rackSyncResult.synced;
      result.rackOperationsFailed = rackSyncResult.failed;
      if (rackSyncResult.errors.length > 0) {
        result.errors.push(...rackSyncResult.errors);
      }
      result.syncedCount += rackSyncResult.synced;
      result.failedCount += rackSyncResult.failed;

      // Sync offline small UPS operations (#996)
      const smallUpsSyncResult = await this.syncOfflineSmallUpsOperations();
      result.smallUpsOperationsSynced = smallUpsSyncResult.synced;
      result.smallUpsOperationsFailed = smallUpsSyncResult.failed;
      if (smallUpsSyncResult.errors.length > 0) {
        result.errors.push(...smallUpsSyncResult.errors);
      }
      result.syncedCount += smallUpsSyncResult.synced;
      result.failedCount += smallUpsSyncResult.failed;

      // Sync offline cooling system operations (#996 Phase 2)
      const coolingSystemSyncResult =
        await this.syncOfflineCoolingSystemOperations();
      result.coolingSystemOperationsSynced = coolingSystemSyncResult.synced;
      result.coolingSystemOperationsFailed = coolingSystemSyncResult.failed;
      if (coolingSystemSyncResult.errors.length > 0) {
        result.errors.push(...coolingSystemSyncResult.errors);
      }
      result.syncedCount += coolingSystemSyncResult.synced;
      result.failedCount += coolingSystemSyncResult.failed;

      // Sync offline liquid cooling CDU operations (#996 Phase 3)
      const liquidCoolingCduSyncResult =
        await this.syncOfflineLiquidCoolingCduOperations();
      result.liquidCoolingCduOperationsSynced =
        liquidCoolingCduSyncResult.synced;
      result.liquidCoolingCduOperationsFailed =
        liquidCoolingCduSyncResult.failed;
      if (liquidCoolingCduSyncResult.errors.length > 0) {
        result.errors.push(...liquidCoolingCduSyncResult.errors);
      }
      result.syncedCount += liquidCoolingCduSyncResult.synced;
      result.failedCount += liquidCoolingCduSyncResult.failed;

      // Sync offline large UPS operations (#996 Phase 4)
      const largeUpsSyncResult = await this.syncOfflineLargeUpsOperations();
      result.largeUpsOperationsSynced = largeUpsSyncResult.synced;
      result.largeUpsOperationsFailed = largeUpsSyncResult.failed;
      if (largeUpsSyncResult.errors.length > 0) {
        result.errors.push(...largeUpsSyncResult.errors);
      }
      result.syncedCount += largeUpsSyncResult.synced;
      result.failedCount += largeUpsSyncResult.failed;

      // Sync offline power distribution operations (#996)
      const powerDistributionSyncResult =
        await this.syncOfflinePowerDistributionOperations();
      result.powerDistributionOperationsSynced =
        powerDistributionSyncResult.synced;
      result.powerDistributionOperationsFailed =
        powerDistributionSyncResult.failed;
      if (powerDistributionSyncResult.errors.length > 0) {
        result.errors.push(...powerDistributionSyncResult.errors);
      }
      result.syncedCount += powerDistributionSyncResult.synced;
      result.failedCount += powerDistributionSyncResult.failed;

      // Replay queued read markers (#1575). Last: a read marker is about what the
      // person already saw, so it must not delay any write, and nothing above
      // depends on it. Its counts stay out of syncedCount/failedCount — the sync
      // indicator speaks about the user's unsaved work, and a read marker is not
      // that; counting it would show "1 item failed to sync" for something the
      // person never asked to save.
      // Guarded on its own: a read marker is the least important thing in this
      // pass, and nothing it can do may turn the user's successful save into a
      // failed sync. The per-entry errors are already handled inside.
      try {
        const readSyncResult = await this.syncOfflineReadOperations();
        result.readOperationsSynced = readSyncResult.synced;
        result.readOperationsFailed = readSyncResult.failed;
        result.readOperationsDropped = readSyncResult.dropped;
      } catch (error) {
        console.error('Read marker sync failed (non-fatal):', error);
      }

      // Update last sync time
      await offlineCache.setLastSyncTime();

      // Notify listeners
      this.notifyListeners(result);

      console.log(
        `Sync completed: ${result.syncedCount} successful, ${result.failedCount} failed`,
      );

      return result;
    } catch (error) {
      console.error('Sync process error:', error);
      result.success = false;
      result.errors.push(
        error instanceof Error ? error.message : 'Unknown sync error',
      );
      return result;
    } finally {
      // Clear timeout and reset sync status
      if (this.syncTimeoutId) {
        clearTimeout(this.syncTimeoutId);
        this.syncTimeoutId = null;
      }
      this.isSyncing = false;
    }
  };

  /**
   * Refresh cache with latest data from server
   * IMPORTANT: Preserves existing rackConfigurations since getAllRequests() doesn't include them
   */
  refreshCache = async (): Promise<void> => {
    if (!networkService.isConnected()) {
      console.log('Cannot refresh cache - no network connection');
      return;
    }

    try {
      console.log('Refreshing offline cache...');

      // Get existing cached data to preserve rack configurations
      const { requests: existingRequests } =
        await offlineCache.getCachedRequests();

      // Build a map of existing rack configurations by request ID
      const existingRacksMap = new Map<string, RackConfiguration[]>();
      // Build a map of existing small UPS configurations by request ID (#996)
      const existingSmallUpsMap = new Map<string, SmallUpsConfiguration[]>();
      // Build a map of existing cooling system configurations by request ID (#996 Phase 2)
      const existingCoolingSystemsMap = new Map<
        string,
        CoolingSystemConfiguration[]
      >();
      // Build a map of existing liquid cooling CDU configurations by request ID (#996 Phase 3)
      const existingLiquidCoolingCdusMap = new Map<
        string,
        LiquidCoolingCduConfiguration[]
      >();
      // Build a map of existing large UPS configurations by request ID (#996 Phase 4)
      const existingLargeUpsMap = new Map<string, LargeUpsConfiguration[]>();
      // Build a map of existing power distribution configurations by request ID (#996)
      const existingPowerDistributionMap = new Map<
        string,
        PowerDistributionConfiguration[]
      >();
      for (const req of existingRequests) {
        if (req.rackConfigurations && req.rackConfigurations.length > 0) {
          existingRacksMap.set(req.id, req.rackConfigurations);
        }
        if (
          req.smallUpsConfigurations &&
          req.smallUpsConfigurations.length > 0
        ) {
          existingSmallUpsMap.set(req.id, req.smallUpsConfigurations);
        }
        if (
          req.coolingSystemConfigurations &&
          req.coolingSystemConfigurations.length > 0
        ) {
          existingCoolingSystemsMap.set(
            req.id,
            req.coolingSystemConfigurations,
          );
        }
        if (
          req.liquidCoolingCduConfigurations &&
          req.liquidCoolingCduConfigurations.length > 0
        ) {
          existingLiquidCoolingCdusMap.set(
            req.id,
            req.liquidCoolingCduConfigurations,
          );
        }
        if (
          req.largeUpsConfigurations &&
          req.largeUpsConfigurations.length > 0
        ) {
          existingLargeUpsMap.set(req.id, req.largeUpsConfigurations);
        }
        if (
          req.powerDistributionConfigurations &&
          req.powerDistributionConfigurations.length > 0
        ) {
          existingPowerDistributionMap.set(
            req.id,
            req.powerDistributionConfigurations,
          );
        }
      }

      // Fetch latest requests from server
      const requests = await RequestsService.getAllRequests();

      // Merge existing rack + small UPS configurations into new requests
      // (when the server list endpoint didn't provide them)
      const mergedRequests = requests.map(req => {
        let merged = req;
        const existingRacks = existingRacksMap.get(req.id);
        if (
          existingRacks &&
          (!req.rackConfigurations || req.rackConfigurations.length === 0)
        ) {
          merged = { ...merged, rackConfigurations: existingRacks };
        }
        const existingSmallUps = existingSmallUpsMap.get(req.id);
        if (
          existingSmallUps &&
          (!req.smallUpsConfigurations ||
            req.smallUpsConfigurations.length === 0)
        ) {
          merged = { ...merged, smallUpsConfigurations: existingSmallUps };
        }
        const existingCoolingSystems = existingCoolingSystemsMap.get(req.id);
        if (
          existingCoolingSystems &&
          (!req.coolingSystemConfigurations ||
            req.coolingSystemConfigurations.length === 0)
        ) {
          merged = {
            ...merged,
            coolingSystemConfigurations: existingCoolingSystems,
          };
        }
        const existingLiquidCoolingCdus = existingLiquidCoolingCdusMap.get(
          req.id,
        );
        if (
          existingLiquidCoolingCdus &&
          (!req.liquidCoolingCduConfigurations ||
            req.liquidCoolingCduConfigurations.length === 0)
        ) {
          merged = {
            ...merged,
            liquidCoolingCduConfigurations: existingLiquidCoolingCdus,
          };
        }
        const existingLargeUps = existingLargeUpsMap.get(req.id);
        if (
          existingLargeUps &&
          (!req.largeUpsConfigurations ||
            req.largeUpsConfigurations.length === 0)
        ) {
          merged = {
            ...merged,
            largeUpsConfigurations: existingLargeUps,
          };
        }
        const existingPowerDistribution = existingPowerDistributionMap.get(
          req.id,
        );
        if (
          existingPowerDistribution &&
          (!req.powerDistributionConfigurations ||
            req.powerDistributionConfigurations.length === 0)
        ) {
          merged = {
            ...merged,
            powerDistributionConfigurations: existingPowerDistribution,
          };
        }
        return merged;
      });

      // Update offline cache
      await offlineCache.cacheRequests(mergedRequests);

      console.log(
        `Cache refreshed with ${mergedRequests.length} requests (preserved ${existingRacksMap.size} rack configs)`,
      );
    } catch (error) {
      console.error('Error refreshing cache:', error);
    }
  };

  /**
   * Full sync - sync offline requests and refresh cache
   * OPTIMIZED: Skip full cache refresh if no requests were synced to reduce load time
   */
  fullSync = async (): Promise<SyncResult> => {
    const syncResult = await this.syncOfflineRequests();

    // Only refresh cache if we have a connection and actually synced something
    // Skip the expensive refreshCache if there was nothing to sync
    // The syncOfflineRequests already updates cache per-request, so this is just a final verification
    if (
      networkService.isConnected() &&
      syncResult.success &&
      syncResult.syncedCount > 0
    ) {
      console.log(
        `✅ Synced ${syncResult.syncedCount} requests - refreshing full cache for verification`,
      );
      await this.refreshCache();
    } else if (syncResult.syncedCount === 0) {
      console.log('⏭️ No requests synced - skipping expensive cache refresh');
    } else if (!networkService.isConnected()) {
      console.log('📴 Offline - skipping cache refresh');
    }

    return syncResult;
  };

  /**
   * Get sync status information
   */
  getSyncStatus = async (): Promise<{
    isSyncing: boolean;
    isOnline: boolean;
    pendingCount: number;
    failedCount: number;
    lastSyncTime: number | null;
  }> => {
    const [pendingRequests, failedRequests, pendingUpdates, lastSyncTime] =
      await Promise.all([
        offlineCache.getPendingOfflineRequests(),
        offlineCache.getFailedOfflineRequests(),
        offlineCache.getOfflineUpdateQueue(),
        offlineCache.getLastSyncTime(),
      ]);

    return {
      isSyncing: this.isSyncing,
      isOnline: networkService.isConnected(),
      pendingCount:
        pendingRequests.length +
        pendingUpdates.filter(u => u.status === 'pending').length,
      failedCount:
        failedRequests.length +
        pendingUpdates.filter(u => u.status === 'failed').length,
      lastSyncTime,
    };
  };

  /**
   * Retry failed offline requests
   */
  retryFailedRequests = async (): Promise<SyncResult> => {
    if (!networkService.isConnected()) {
      return {
        success: false,
        syncedCount: 0,
        failedCount: 0,
        errors: ['No network connection'],
      };
    }

    // Reset failed requests to pending status
    const failedRequests = await offlineCache.getFailedOfflineRequests();

    for (const failedRequest of failedRequests) {
      await offlineCache.updateOfflineRequestStatus(
        failedRequest.id,
        'pending',
      );
    }

    // Now sync them
    return await this.syncOfflineRequests();
  };

  /**
   * Sync offline rack operations
   * Processes queued create/update/delete/reorder operations for rack configurations
   * @see GitLab #995 — Multiple Rack Instances
   */
  syncOfflineRackOperations = async (): Promise<{
    synced: number;
    failed: number;
    errors: string[];
  }> => {
    const result = { synced: 0, failed: 0, errors: [] as string[] };

    if (!networkService.isConnected()) {
      console.log('Cannot sync rack operations - no network connection');
      return result;
    }

    const pendingOps = await offlineCache.getPendingRackOperations();

    if (pendingOps.length === 0) {
      console.log('No offline rack operations to sync');
      return result;
    }

    console.log(`Syncing ${pendingOps.length} offline rack operations...`);

    for (const op of pendingOps) {
      try {
        // Update status to syncing
        await offlineCache.updateRackOperationStatus(op.id, 'syncing');

        switch (op.operation) {
          case 'create': {
            const createdRack = await api.post<RackConfiguration>(
              `/requests/${op.requestId}/racks`,
              op.data,
            );
            // If the data had a temp ID, remap subsequent operations to use the server ID
            const tempId = op.rackId ?? op.data?.id;
            if (
              tempId !== undefined &&
              createdRack.id !== undefined &&
              tempId !== createdRack.id
            ) {
              const remapped = await offlineCache.remapRackIds(
                op.requestId,
                tempId,
                createdRack.id,
              );
              await offlineCache.remapCachedDimensionUnits(
                op.requestId,
                'racks',
                tempId,
                createdRack.id,
              );
              if (remapped > 0) {
                console.log(
                  `✅ Remapped ${remapped} rack ops from tempId ${tempId} to serverId ${createdRack.id}`,
                );
              }
            }
            break;
          }
          case 'update':
            if (op.rackId !== undefined) {
              await api.put<RackConfiguration>(
                `/requests/${op.requestId}/racks/${op.rackId}`,
                op.data,
              );
            }
            break;
          case 'delete':
            if (op.rackId !== undefined) {
              await api.delete(`/requests/${op.requestId}/racks/${op.rackId}`);
            }
            break;
          case 'reorder':
            if (op.rackIds !== undefined) {
              await api.patch<RackConfiguration[]>(
                `/requests/${op.requestId}/racks/reorder`,
                { rackIds: op.rackIds },
              );
            }
            break;
        }

        // Success - remove from queue
        await offlineCache.removeFromOfflineRackQueue(op.id);
        result.synced++;

        console.log(
          `Successfully synced rack operation: ${op.id} (${op.operation})`,
        );
      } catch (error) {
        // Mark as failed
        await offlineCache.updateRackOperationStatus(op.id, 'failed');
        result.failed++;

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(
          `Failed to sync rack ${op.operation} for ${op.requestId}: ${errorMessage}`,
        );

        console.error(`Failed to sync rack operation ${op.id}:`, error);
      }
    }

    console.log(
      `Rack operations sync completed: ${result.synced} successful, ${result.failed} failed`,
    );
    return result;
  };

  /**
   * Sync offline small UPS operations
   * Processes queued create/update/delete/reorder operations for small UPS configurations.
   * PUT for update; reorder body key is `smallUpsIds`.
   * @see GitLab #996 — Repeating Sections (Phase 1: Small UPS)
   */
  syncOfflineSmallUpsOperations = async (): Promise<{
    synced: number;
    failed: number;
    errors: string[];
  }> => {
    const result = { synced: 0, failed: 0, errors: [] as string[] };

    if (!networkService.isConnected()) {
      console.log('Cannot sync small UPS operations - no network connection');
      return result;
    }

    const pendingOps = await offlineCache.getPendingSmallUpsOperations();

    if (pendingOps.length === 0) {
      console.log('No offline small UPS operations to sync');
      return result;
    }

    console.log(`Syncing ${pendingOps.length} offline small UPS operations...`);

    for (const op of pendingOps) {
      try {
        await offlineCache.updateSmallUpsOperationStatus(op.id, 'syncing');

        switch (op.operation) {
          case 'create': {
            const createdUnit = await api.post<SmallUpsConfiguration>(
              `/requests/${op.requestId}/small-ups`,
              op.data,
            );
            // If the queued create carried a temp ID, remap subsequent ops to the server ID.
            const tempId = op.smallUpsId ?? op.data?.id;
            if (
              tempId !== undefined &&
              createdUnit.id !== undefined &&
              tempId !== createdUnit.id
            ) {
              const remapped = await offlineCache.remapSmallUpsIds(
                op.requestId,
                tempId,
                createdUnit.id,
              );
              if (remapped > 0) {
                console.log(
                  `✅ Remapped ${remapped} small UPS ops from tempId ${tempId} to serverId ${createdUnit.id}`,
                );
              }
            }
            break;
          }
          case 'update':
            if (op.smallUpsId !== undefined) {
              await api.put<SmallUpsConfiguration>(
                `/requests/${op.requestId}/small-ups/${op.smallUpsId}`,
                op.data,
              );
            }
            break;
          case 'delete':
            if (op.smallUpsId !== undefined) {
              await api.delete(
                `/requests/${op.requestId}/small-ups/${op.smallUpsId}`,
              );
            }
            break;
          case 'reorder':
            if (op.smallUpsIds !== undefined) {
              await api.patch<SmallUpsConfiguration[]>(
                `/requests/${op.requestId}/small-ups/reorder`,
                { smallUpsIds: op.smallUpsIds },
              );
            }
            break;
        }

        await offlineCache.removeFromOfflineSmallUpsQueue(op.id);
        result.synced++;

        console.log(
          `Successfully synced small UPS operation: ${op.id} (${op.operation})`,
        );
      } catch (error) {
        await offlineCache.updateSmallUpsOperationStatus(op.id, 'failed');
        result.failed++;

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(
          `Failed to sync small UPS ${op.operation} for ${op.requestId}: ${errorMessage}`,
        );

        console.error(`Failed to sync small UPS operation ${op.id}:`, error);
      }
    }

    console.log(
      `Small UPS operations sync completed: ${result.synced} successful, ${result.failed} failed`,
    );
    return result;
  };

  /**
   * Sync offline cooling system operations
   * Processes queued create/update/delete/reorder operations for cooling system configurations.
   * PUT for update; reorder body key is `coolingSystemIds`.
   * @see GitLab #996 — Repeating Sections (Phase 2: Cooling)
   */
  syncOfflineCoolingSystemOperations = async (): Promise<{
    synced: number;
    failed: number;
    errors: string[];
  }> => {
    const result = { synced: 0, failed: 0, errors: [] as string[] };

    if (!networkService.isConnected()) {
      console.log(
        'Cannot sync cooling system operations - no network connection',
      );
      return result;
    }

    const pendingOps = await offlineCache.getPendingCoolingSystemOperations();

    if (pendingOps.length === 0) {
      console.log('No offline cooling system operations to sync');
      return result;
    }

    console.log(
      `Syncing ${pendingOps.length} offline cooling system operations...`,
    );

    for (const op of pendingOps) {
      try {
        await offlineCache.updateCoolingSystemOperationStatus(op.id, 'syncing');

        switch (op.operation) {
          case 'create': {
            const createdUnit = await api.post<CoolingSystemConfiguration>(
              `/requests/${op.requestId}/cooling-systems`,
              op.data,
            );
            // If the queued create carried a temp ID, remap subsequent ops to the server ID.
            const tempId = op.coolingSystemId ?? op.data?.id;
            if (
              tempId !== undefined &&
              createdUnit.id !== undefined &&
              tempId !== createdUnit.id
            ) {
              const remapped = await offlineCache.remapCoolingSystemIds(
                op.requestId,
                tempId,
                createdUnit.id,
              );
              await offlineCache.remapCachedDimensionUnits(
                op.requestId,
                'coolingSystems',
                tempId,
                createdUnit.id,
              );
              if (remapped > 0) {
                console.log(
                  `✅ Remapped ${remapped} cooling system ops from tempId ${tempId} to serverId ${createdUnit.id}`,
                );
              }
            }
            break;
          }
          case 'update':
            if (op.coolingSystemId !== undefined) {
              await api.put<CoolingSystemConfiguration>(
                `/requests/${op.requestId}/cooling-systems/${op.coolingSystemId}`,
                op.data,
              );
            }
            break;
          case 'delete':
            if (op.coolingSystemId !== undefined) {
              await api.delete(
                `/requests/${op.requestId}/cooling-systems/${op.coolingSystemId}`,
              );
            }
            break;
          case 'reorder':
            if (op.coolingSystemIds !== undefined) {
              await api.patch<CoolingSystemConfiguration[]>(
                `/requests/${op.requestId}/cooling-systems/reorder`,
                { coolingSystemIds: op.coolingSystemIds },
              );
            }
            break;
        }

        await offlineCache.removeFromOfflineCoolingSystemQueue(op.id);
        result.synced++;

        console.log(
          `Successfully synced cooling system operation: ${op.id} (${op.operation})`,
        );
      } catch (error) {
        await offlineCache.updateCoolingSystemOperationStatus(op.id, 'failed');
        result.failed++;

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(
          `Failed to sync cooling system ${op.operation} for ${op.requestId}: ${errorMessage}`,
        );

        console.error(
          `Failed to sync cooling system operation ${op.id}:`,
          error,
        );
      }
    }

    console.log(
      `Cooling system operations sync completed: ${result.synced} successful, ${result.failed} failed`,
    );
    return result;
  };

  /**
   * Sync offline liquid cooling CDU operations
   * Processes queued create/update/delete/reorder operations for liquid cooling CDU configurations.
   * PUT for update; reorder body key is `liquidCoolingCduIds`.
   * @see GitLab #996 — Repeating Sections (Phase 3: Liquid Cooling)
   */
  syncOfflineLiquidCoolingCduOperations = async (): Promise<{
    synced: number;
    failed: number;
    errors: string[];
  }> => {
    const result = { synced: 0, failed: 0, errors: [] as string[] };

    if (!networkService.isConnected()) {
      console.log(
        'Cannot sync liquid cooling CDU operations - no network connection',
      );
      return result;
    }

    const pendingOps =
      await offlineCache.getPendingLiquidCoolingCduOperations();

    if (pendingOps.length === 0) {
      console.log('No offline liquid cooling CDU operations to sync');
      return result;
    }

    console.log(
      `Syncing ${pendingOps.length} offline liquid cooling CDU operations...`,
    );

    for (const op of pendingOps) {
      try {
        await offlineCache.updateLiquidCoolingCduOperationStatus(
          op.id,
          'syncing',
        );

        switch (op.operation) {
          case 'create': {
            const createdUnit = await api.post<LiquidCoolingCduConfiguration>(
              `/requests/${op.requestId}/liquid-cooling-cdus`,
              op.data,
            );
            // If the queued create carried a temp ID, remap subsequent ops to the server ID.
            const tempId = op.liquidCoolingCduId ?? op.data?.id;
            if (
              tempId !== undefined &&
              createdUnit.id !== undefined &&
              tempId !== createdUnit.id
            ) {
              const remapped = await offlineCache.remapLiquidCoolingCduIds(
                op.requestId,
                tempId,
                createdUnit.id,
              );
              if (remapped > 0) {
                console.log(
                  `✅ Remapped ${remapped} liquid cooling CDU ops from tempId ${tempId} to serverId ${createdUnit.id}`,
                );
              }
            }
            break;
          }
          case 'update':
            if (op.liquidCoolingCduId !== undefined) {
              await api.put<LiquidCoolingCduConfiguration>(
                `/requests/${op.requestId}/liquid-cooling-cdus/${op.liquidCoolingCduId}`,
                op.data,
              );
            }
            break;
          case 'delete':
            if (op.liquidCoolingCduId !== undefined) {
              await api.delete(
                `/requests/${op.requestId}/liquid-cooling-cdus/${op.liquidCoolingCduId}`,
              );
            }
            break;
          case 'reorder':
            if (op.liquidCoolingCduIds !== undefined) {
              await api.patch<LiquidCoolingCduConfiguration[]>(
                `/requests/${op.requestId}/liquid-cooling-cdus/reorder`,
                { liquidCoolingCduIds: op.liquidCoolingCduIds },
              );
            }
            break;
        }

        await offlineCache.removeFromOfflineLiquidCoolingCduQueue(op.id);
        result.synced++;

        console.log(
          `Successfully synced liquid cooling CDU operation: ${op.id} (${op.operation})`,
        );
      } catch (error) {
        await offlineCache.updateLiquidCoolingCduOperationStatus(
          op.id,
          'failed',
        );
        result.failed++;

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(
          `Failed to sync liquid cooling CDU ${op.operation} for ${op.requestId}: ${errorMessage}`,
        );

        console.error(
          `Failed to sync liquid cooling CDU operation ${op.id}:`,
          error,
        );
      }
    }

    console.log(
      `Liquid cooling CDU operations sync completed: ${result.synced} successful, ${result.failed} failed`,
    );
    return result;
  };

  /**
   * Sync offline large UPS operations
   * Processes queued create/update/delete/reorder operations for large UPS configurations.
   * PUT for update; reorder body key is `largeUpsIds`.
   * @see GitLab #996 — Repeating Sections (Phase 4: Facility Power / Large UPS)
   */
  syncOfflineLargeUpsOperations = async (): Promise<{
    synced: number;
    failed: number;
    errors: string[];
  }> => {
    const result = { synced: 0, failed: 0, errors: [] as string[] };

    if (!networkService.isConnected()) {
      console.log('Cannot sync large UPS operations - no network connection');
      return result;
    }

    const pendingOps = await offlineCache.getPendingLargeUpsOperations();

    if (pendingOps.length === 0) {
      console.log('No offline large UPS operations to sync');
      return result;
    }

    console.log(`Syncing ${pendingOps.length} offline large UPS operations...`);

    for (const op of pendingOps) {
      try {
        await offlineCache.updateLargeUpsOperationStatus(op.id, 'syncing');

        switch (op.operation) {
          case 'create': {
            const createdUnit = await api.post<LargeUpsConfiguration>(
              `/requests/${op.requestId}/large-ups`,
              op.data,
            );
            // If the queued create carried a temp ID, remap subsequent ops to the server ID.
            const tempId = op.largeUpsId ?? op.data?.id;
            if (
              tempId !== undefined &&
              createdUnit.id !== undefined &&
              tempId !== createdUnit.id
            ) {
              const remapped = await offlineCache.remapLargeUpsIds(
                op.requestId,
                tempId,
                createdUnit.id,
              );
              if (remapped > 0) {
                console.log(
                  `✅ Remapped ${remapped} large UPS ops from tempId ${tempId} to serverId ${createdUnit.id}`,
                );
              }
            }
            break;
          }
          case 'update':
            if (op.largeUpsId !== undefined) {
              await api.put<LargeUpsConfiguration>(
                `/requests/${op.requestId}/large-ups/${op.largeUpsId}`,
                op.data,
              );
            }
            break;
          case 'delete':
            if (op.largeUpsId !== undefined) {
              await api.delete(
                `/requests/${op.requestId}/large-ups/${op.largeUpsId}`,
              );
            }
            break;
          case 'reorder':
            if (op.largeUpsIds !== undefined) {
              await api.patch<LargeUpsConfiguration[]>(
                `/requests/${op.requestId}/large-ups/reorder`,
                { largeUpsIds: op.largeUpsIds },
              );
            }
            break;
        }

        await offlineCache.removeFromOfflineLargeUpsQueue(op.id);
        result.synced++;

        console.log(
          `Successfully synced large UPS operation: ${op.id} (${op.operation})`,
        );
      } catch (error) {
        await offlineCache.updateLargeUpsOperationStatus(op.id, 'failed');
        result.failed++;

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(
          `Failed to sync large UPS ${op.operation} for ${op.requestId}: ${errorMessage}`,
        );

        console.error(`Failed to sync large UPS operation ${op.id}:`, error);
      }
    }

    console.log(
      `Large UPS operations sync completed: ${result.synced} successful, ${result.failed} failed`,
    );
    return result;
  };

  /**
   * Sync offline power distribution operations
   * Processes queued create/update/delete/reorder operations for power distribution configurations.
   * PUT for update; reorder body key is `powerDistributionIds`.
   * @see GitLab #996 — Repeating Sections (Facility Power / Power Distribution)
   */
  syncOfflinePowerDistributionOperations = async (): Promise<{
    synced: number;
    failed: number;
    errors: string[];
  }> => {
    const result = { synced: 0, failed: 0, errors: [] as string[] };

    if (!networkService.isConnected()) {
      console.log(
        'Cannot sync power distribution operations - no network connection',
      );
      return result;
    }

    const pendingOps =
      await offlineCache.getPendingPowerDistributionOperations();

    if (pendingOps.length === 0) {
      console.log('No offline power distribution operations to sync');
      return result;
    }

    console.log(
      `Syncing ${pendingOps.length} offline power distribution operations...`,
    );

    for (const op of pendingOps) {
      try {
        await offlineCache.updatePowerDistributionOperationStatus(
          op.id,
          'syncing',
        );

        switch (op.operation) {
          case 'create': {
            const createdUnit = await api.post<PowerDistributionConfiguration>(
              `/requests/${op.requestId}/power-distribution`,
              op.data,
            );
            // If the queued create carried a temp ID, remap subsequent ops to the server ID.
            const tempId = op.powerDistributionId ?? op.data?.id;
            if (
              tempId !== undefined &&
              createdUnit.id !== undefined &&
              tempId !== createdUnit.id
            ) {
              const remapped = await offlineCache.remapPowerDistributionIds(
                op.requestId,
                tempId,
                createdUnit.id,
              );
              await offlineCache.remapCachedDimensionUnits(
                op.requestId,
                'powerDistribution',
                tempId,
                createdUnit.id,
              );
              if (remapped > 0) {
                console.log(
                  `✅ Remapped ${remapped} power distribution ops from tempId ${tempId} to serverId ${createdUnit.id}`,
                );
              }
            }
            break;
          }
          case 'update':
            if (op.powerDistributionId !== undefined) {
              await api.put<PowerDistributionConfiguration>(
                `/requests/${op.requestId}/power-distribution/${op.powerDistributionId}`,
                op.data,
              );
            }
            break;
          case 'delete':
            if (op.powerDistributionId !== undefined) {
              await api.delete(
                `/requests/${op.requestId}/power-distribution/${op.powerDistributionId}`,
              );
            }
            break;
          case 'reorder':
            if (op.powerDistributionIds !== undefined) {
              await api.patch<PowerDistributionConfiguration[]>(
                `/requests/${op.requestId}/power-distribution/reorder`,
                { powerDistributionIds: op.powerDistributionIds },
              );
            }
            break;
        }

        await offlineCache.removeFromOfflinePowerDistributionQueue(op.id);
        result.synced++;

        console.log(
          `Successfully synced power distribution operation: ${op.id} (${op.operation})`,
        );
      } catch (error) {
        await offlineCache.updatePowerDistributionOperationStatus(
          op.id,
          'failed',
        );
        result.failed++;

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(
          `Failed to sync power distribution ${op.operation} for ${op.requestId}: ${errorMessage}`,
        );

        console.error(
          `Failed to sync power distribution operation ${op.id}:`,
          error,
        );
      }
    }

    console.log(
      `Power distribution operations sync completed: ${result.synced} successful, ${result.failed} failed`,
    );
    return result;
  };

  /**
   * Sync offline location tag operations (#999)
   */
  syncOfflineLocationTagOperations = async (): Promise<{
    synced: number;
    failed: number;
    errors: string[];
    nameConflicts: string[];
    requestIds: string[];
  }> => {
    const result = {
      synced: 0,
      failed: 0,
      errors: [] as string[],
      nameConflicts: [] as string[],
      requestIds: [] as string[],
    };

    if (!networkService.isConnected()) {
      return result;
    }

    const pendingOps = await offlineCache.getPendingLocationTagOperations();
    if (pendingOps.length === 0) {
      return result;
    }

    console.log(
      `Syncing ${pendingOps.length} offline location tag operations...`,
    );

    for (const op of pendingOps) {
      try {
        await offlineCache.updateLocationTagOperationStatus(op.id, 'syncing');

        switch (op.operation) {
          case 'create': {
            if (!op.data?.name) {
              break;
            }
            const created = await api.post<{ locationTag: { id?: number } }>(
              `/requests/${op.requestId}/location-tags`,
              { name: op.data.name },
            );
            const tempId = op.tagId;
            if (
              tempId !== undefined &&
              created.locationTag.id !== undefined &&
              tempId !== created.locationTag.id
            ) {
              await offlineCache.remapLocationTagIds(
                op.requestId,
                tempId,
                created.locationTag.id,
              );
            }
            break;
          }
          case 'update':
            if (op.tagId !== undefined && op.data?.name) {
              await api.patch(
                `/requests/${op.requestId}/location-tags/${op.tagId}`,
                { name: op.data.name },
              );
            }
            break;
          case 'delete':
            if (op.tagId !== undefined) {
              await api.delete(
                `/requests/${op.requestId}/location-tags/${op.tagId}`,
              );
            }
            break;
        }

        await offlineCache.removeFromOfflineLocationTagQueue(op.id);
        result.synced++;
        if (!result.requestIds.includes(op.requestId)) {
          result.requestIds.push(op.requestId);
        }

        const refreshed = await api.get<LocationTagsResponse>(
          `/requests/${op.requestId}/location-tags`,
        );
        await mirrorLocationTagsToOfflineCache(
          op.requestId,
          refreshed.locationTags,
        );
      } catch (error) {
        await offlineCache.updateLocationTagOperationStatus(op.id, 'failed');
        result.failed++;

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(
          `Failed to sync location tag ${op.operation} for ${op.requestId}: ${errorMessage}`,
        );

        if (isLocationTagNameConflictError(error) && op.data?.name) {
          result.nameConflicts.push(op.data.name);
        }

        console.error(`Failed to sync location tag operation ${op.id}:`, error);
      }
    }

    console.log(
      `Location tag operations sync completed: ${result.synced} successful, ${result.failed} failed`,
    );
    return result;
  };

  /**
   * Replay queued read markers (#1575 story 3.2).
   *
   * ⚠️ EACH ENTRY REPLAYS WITH ITS OWN `readAt`, NEVER WITH `now` (AC 2). The
   * server's upsert is monotonic, so an old marker arriving late is harmless; a
   * marker stamped at replay time, by contrast, would hide every change made
   * between the open and the reconnect.
   *
   * ⚠️ A 4xx DROPS THE ENTRY (AC 4). `INVALID_READ_AT` is the case story 1.2
   * flagged: a device whose clock ran ahead queued an instant the server will
   * refuse for as long as it stays ahead, so retrying it is retrying for ever.
   * `403` (the person has since lost their claim) and `401` are the same shape of
   * answer — the server has spoken, and no later attempt changes it. Everything
   * else (offline mid-drain, a 5xx) keeps the entry for the next pass, which is
   * the whole point of a queue that survives a restart.
   */
  syncOfflineReadOperations = async (): Promise<{
    synced: number;
    failed: number;
    dropped: number;
    errors: string[];
  }> => {
    const result = {
      synced: 0,
      failed: 0,
      dropped: 0,
      errors: [] as string[],
    };

    if (!networkService.isConnected()) {
      return result;
    }

    const pendingOps = await offlineCache.getPendingReadOperations();
    if (pendingOps.length === 0) {
      return result;
    }

    console.log(`Syncing ${pendingOps.length} offline read markers...`);

    for (const op of pendingOps) {
      try {
        await RequestsService.markRead(op.requestId, op.readAt);
        await offlineCache.removeFromOfflineReadQueue(op.id);
        clearCachedUnseenChanges(op.requestId);
        result.synced++;
      } catch (error) {
        const permanent =
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500;

        if (permanent) {
          await offlineCache.removeFromOfflineReadQueue(op.id);
          result.dropped++;
        } else {
          result.failed++;
        }

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(
          `Failed to sync read marker for ${op.requestId}: ${errorMessage}`,
        );
        // Logged here, not pushed onto SyncResult.errors: that array feeds the
        // sync indicator, and a read marker must never make the user's own work
        // look like it failed to save.
        console.error(
          `Failed to sync read marker ${op.id} (${
            permanent ? 'dropped' : 'retained'
          }):`,
          errorMessage,
        );
      }
    }

    console.log(
      `Read marker sync completed: ${result.synced} successful, ${result.failed} retained, ${result.dropped} dropped`,
    );
    return result;
  };

  /**
   * Add listener for sync events
   */
  addSyncListener(listener: (result: SyncResult) => void): () => void {
    this.syncListeners.push(listener);

    return () => {
      const index = this.syncListeners.indexOf(listener);
      if (index > -1) {
        this.syncListeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(result: SyncResult): void {
    this.syncListeners.forEach(listener => {
      try {
        listener(result);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });
  }

  /**
   * Check if sync is currently running
   */
  isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  /**
   * Force reset sync status (for debugging stuck syncs)
   * This should only be used if the sync indicator gets stuck
   */
  forceResetSyncStatus(): void {
    console.warn('⚠️ SyncService: Force resetting sync status');
    this.isSyncing = false;
  }
}

export const syncService = new SyncService();
